import { useMemo } from 'react'
import { BlockTypeEnum } from '@omnea-app/core/blocks'
import { ValidUuidString, isUuid } from '@omnea-app/legacy/utils/string/isUuid'
import { RequestStepStateEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { RequestPriorityEnum } from '@omnea-app/requests/requestPriority'
import { TaskStatusEnum } from '@omnea-app/requests/requestsFrontendClient'
import { TagWithIdAndName } from '@omnea-app/tags/tagsFrontendClient'
import { TasksTopLevelFilterKey } from '@omnea-app/tasks/tasksFrontendClient'
import { Task } from '@omnea-app/tasks/tasksHttpClient'
import { useTasks } from '../useTasks'

export type TaskRow = {
  id: string
  requestId: string
  stageType: BlockTypeEnum
  name: string
  label: string
  supplier: { name?: string; logoUrl?: string | null } | null
  type: string
  dueDate: Date | null
  createdAt: string
  state: RequestStepStateEnum
  stage: number
  totalStages: number
  status: TaskStatusEnum
  isRejected: boolean
  activeAmendmentRequests: { id: string }[]
  buyerLegalEntity: Task['request']['buyerLegalEntity']
  value: Task['request']['value']
  owner: Task['request']['participants']['projectOwner']
  requestor: Task['request']['participants']['requestor']
  tags: TagWithIdAndName[]
  autoGeneratedTags: TagWithIdAndName[]
  priority: RequestPriorityEnum
  currentUserAssignees: Task['currentUserAssignees'] | null
  currentGroupAssignees: Task['currentGroupAssignees'] | null
  allCurrentAssignees: string[]
  activeSubTaskIds: string[]
}

export const useTaskTableRowsForCurrentUser = (
  topLevelFilterKeyProp: TasksTopLevelFilterKey | ValidUuidString
): { rows: TaskRow[]; loading: boolean } => {
  const topLevelFilterKey = useMemo(() => {
    if (isUuid(topLevelFilterKeyProp)) {
      return null
    }
    return topLevelFilterKeyProp
  }, [topLevelFilterKeyProp])

  const { data, isLoading: loading } = useTasks({ topLevelFilterKey })
  const rows = (data || []).map((task) => {
    return {
      id: task.id,
      requestId: task.request.id,
      stageType: task.stageType as BlockTypeEnum,
      state: task.state as RequestStepStateEnum,
      stage: task.stage,
      totalStages: task.totalStages,
      status: task.status as TaskStatusEnum,
      isRejected: task.isRejected,
      name: task.request.name,
      label: task.stepLabel,
      supplier: {
        name: task.request.supplierName,
        logoUrl: task.request.supplierLogoUrl,
      },
      publicId: task.request.publicId,
      department: task.request.departmentName,
      type: task.request.workflowTitle,
      dueDate: new Date(task.dueDate),
      createdAt: task.createdAt,
      activeAmendmentRequests: task.activeAmendments,
      tags: task.request.tags,
      autoGeneratedTags: task.request.autoGeneratedTags,
      priority: task.request.priority as RequestPriorityEnum,
      buyerLegalEntity: task.request.buyerLegalEntity,
      value: task.request.value,
      owner: task.request.participants.projectOwner,
      requestor: task.request.participants.requestor,
      currentUserAssignees: task.currentUserAssignees,
      currentGroupAssignees: task.currentGroupAssignees,
      allCurrentAssignees: [
        ...task.currentUserAssignees.map((u) => `${u.firstName} ${u.lastName}`),
        ...task.currentGroupAssignees.map((g) => g.name),
      ],
      activeSubTaskIds: (task.subTasks || []).filter((subTask) => !subTask.completedOn).map((subTask) => subTask.id),
    }
  })

  return {
    rows,
    loading,
  }
}
